import { Head, Link, router, useForm } from "@inertiajs/react"
import {
  BookOpen,
  Search,
  Plus,
  FileText,
  CheckCircle,
  Clock,
  Trash2,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const coursesTabs = [
  { label: "Courses", href: "/app/admin/courses" },
  { label: "Pending Review", href: "/app/admin/courses/pending_review" },
  { label: "Tracks", href: "/app/admin/learning_tracks" },
  { label: "Categories", href: "/app/admin/course_categories" },
]
import { Button } from "@/components/ui/button"
import { FileUploadField } from "@/components/ui/file-upload-field"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"
import { CourseCoverImage } from "@/components/CourseCoverImage"

const COURSE_STATUS_ORDER = { draft: 0, published: 1 }
const VISIBILITY_ORDER = { all_members: 0, coaches_only: 1 }

function courseCompareWithNullsLast(a, b, direction) {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (a < b) return direction === "asc" ? -1 : 1
  if (a > b) return direction === "asc" ? 1 : -1
  return 0
}
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  if (status === "published") {
    return (
      <Badge className="bg-green-500 text-white hover:bg-green-500">
        Published
      </Badge>
    )
  }
  return <Badge variant="secondary">Draft</Badge>
}

function VisibilityLabel({ visibility }) {
  if (visibility === "coaches_only") {
    return (
      <span className="text-xs text-amber-600 font-medium">Coaches Only</span>
    )
  }
  return <span className="text-xs text-zinc-400">All Members</span>
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// ---------------------------------------------------------------------------
// Stat cards
// ---------------------------------------------------------------------------

const statCards = [
  { key: null, label: "Total", statKey: "total" },
  { key: "published", label: "Published", statKey: "published" },
  { key: "draft", label: "Drafts", statKey: "drafts" },
]

// ---------------------------------------------------------------------------
// Course Form Dialog (Create / Edit)
// ---------------------------------------------------------------------------

function CourseFormDialog({
  open,
  onOpenChange,
  course = null,
  categories = [],
}) {
  const isEditing = !!course
  const defaultCategoryId = course?.category?.id
    ? String(course.category.id)
    : (categories[0]?.id ? String(categories[0].id) : "")
  const form = useForm({
    title: course?.title || "",
    description: course?.description || "",
    course_category_id: defaultCategoryId,
    visibility: course?.visibility || "all_members",
    status: course?.status || "draft",
    cover_image: null,
    _purge_cover_image: false,
  })
  const { transform } = form

  // This dialog both creates and edits, so it carries the same task #524
  // guard as the Show page's edit dialog: a null cover_image serializes to ""
  // under forceFormData, which Active Storage treats as an explicit delete.
  useEffect(() => {
    transform((data) => {
      if (data.cover_image instanceof File) return data
      const { cover_image, ...rest } = data
      return rest
    })
  }, [transform])

  const handleSubmit = (e) => {
    e.preventDefault()

    // onSuccess is the ONLY thing that closes the dialog. A validation failure
    // (e.g. publishing a brand-new course with no published lesson, or a
    // rejected cover image) comes back as an Inertia error payload: onSuccess
    // does not fire, the dialog stays open, and `form.errors` renders inline
    // (task #338.1 — same silent-close class as task #335).
    const options = {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    }

    if (isEditing) {
      form.patch(`/app/admin/courses/${course.id}`, options)
    } else {
      form.post("/app/admin/courses", options)
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Course" : "New Course"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update course details."
                : "Create a new course for the LMS."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            {/* Base / general error (e.g. anything the model attaches to :base) */}
            {form.errors.base && (
              <p
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {form.errors.base}
              </p>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="course-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="course-title"
                placeholder="Course title"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="course-description">Description</Label>
              <Textarea
                id="course-description"
                placeholder="Optional description..."
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[80px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.data.course_category_id}
                onValueChange={(v) => form.setData("course_category_id", v)}
                required
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.errors.course_category_id && (
                <p className="text-sm text-red-600">
                  {form.errors.course_category_id}
                </p>
              )}
            </div>

            {/* Visibility */}
            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select
                value={form.data.visibility}
                onValueChange={(v) => form.setData("visibility", v)}
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_members">All Members</SelectItem>
                  <SelectItem value="coaches_only">Coaches Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.data.status}
                onValueChange={(v) => form.setData("status", v)}
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
              {/* Publishing a brand-new course with no published lesson is
                  rejected by the model (Course#must_have_published_lesson_to_publish),
                  which attaches the error to :status. Surface it right here so
                  the admin sees WHY instead of a silent close. */}
              {form.errors.status && (
                <p className="text-sm text-red-600">{form.errors.status}</p>
              )}
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <Label htmlFor="course-cover-image">Cover Image</Label>
              <FileUploadField
                id="course-cover-image"
                accept="image/webp,image/png,image/jpeg"
                value={form.data.cover_image}
                onChange={(f) => form.setData("cover_image", f)}
                previewSrc={isEditing ? course?.coverImageUrl : null}
                existingFileUrl={isEditing ? course?.coverImageUrl : null}
                pendingRemoval={form.data._purge_cover_image}
                onRequestRemoveExisting={
                  isEditing
                    ? () => form.setData("_purge_cover_image", true)
                    : undefined
                }
                onUndoRemoveExisting={() =>
                  form.setData("_purge_cover_image", false)
                }
                helpText="WebP, PNG, or JPEG. Max 5 MB."
              />
              {form.errors.cover_image && (
                <p className="text-sm text-red-600">
                  {form.errors.cover_image}
                </p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                ? "Save Changes"
                : "Create Course"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Delete Confirmation Dialog
// ---------------------------------------------------------------------------

function DeleteCourseDialog({ open, onOpenChange, course }) {
  const [processing, setProcessing] = useState(false)

  const handleDelete = () => {
    setProcessing(true)
    router.delete(`/app/admin/courses/${course.id}`, {
      preserveScroll: true,
      onFinish: () => {
        setProcessing(false)
        onOpenChange(false)
      },
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Course</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &ldquo;{course?.title}&rdquo;? This
            will also remove all lessons, questions, and resources associated
            with this course. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={processing}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {processing ? "Deleting..." : "Delete Course"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function CoursesIndex({
  currentUser,
  courses,
  stats,
  categories,
  filters,
  pagination,
  pendingCount,
  pendingPublicReviewCount = 0,
}) {
  const [searchQuery, setSearchQuery] = useState(filters?.search || "")
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteCourse, setDeleteCourse] = useState(null)
  const [sortKey, setSortKey] = useState("updatedAt")
  const [direction, setDirection] = useState("desc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    setSortKey("updatedAt")
    setDirection("desc")
  }

  // Client-side sort over the current page of courses. Server already filters
  // and paginates — sort is purely a UX nicety for the visible window.
  const sortedCourses = useMemo(() => {
    if (!courses) return []
    const arr = [...courses]
    arr.sort((a, b) => {
      let aVal
      let bVal
      if (sortKey === "title") {
        aVal = a.title?.toLowerCase() || null
        bVal = b.title?.toLowerCase() || null
      } else if (sortKey === "status") {
        aVal = a.status != null ? COURSE_STATUS_ORDER[a.status] : null
        bVal = b.status != null ? COURSE_STATUS_ORDER[b.status] : null
      } else if (sortKey === "category") {
        aVal = a.category?.name?.toLowerCase() || null
        bVal = b.category?.name?.toLowerCase() || null
      } else if (sortKey === "visibility") {
        aVal = a.visibility != null ? VISIBILITY_ORDER[a.visibility] : null
        bVal = b.visibility != null ? VISIBILITY_ORDER[b.visibility] : null
      } else if (sortKey === "lessons") {
        aVal = a.lessonsCount ?? null
        bVal = b.lessonsCount ?? null
      } else if (sortKey === "createdBy") {
        aVal = a.createdBy?.name?.toLowerCase() || null
        bVal = b.createdBy?.name?.toLowerCase() || null
      } else if (sortKey === "updatedAt") {
        aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : null
        bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : null
      }
      return courseCompareWithNullsLast(aVal, bVal, direction)
    })
    return arr
  }, [courses, sortKey, direction])

  // Build filter params, preserving active filters
  const buildParams = (overrides = {}) => {
    const p = {}
    const status =
      "status" in overrides ? overrides.status : filters?.currentStatus
    const categoryId =
      "category_id" in overrides
        ? overrides.category_id
        : filters?.currentCategory
    const search =
      "search" in overrides ? overrides.search : filters?.search
    const page = "page" in overrides ? overrides.page : undefined

    if (status) p.status = status
    if (categoryId) p.category_id = categoryId
    if (search) p.search = search
    if (page && page > 1) p.page = page
    return p
  }

  const handleStatusFilter = (status) => {
    router.get(
      "/app/admin/courses",
      buildParams({ status: status || undefined, page: 1 }),
      { preserveState: true }
    )
  }

  const handleCategoryFilter = (categoryId) => {
    const value = categoryId === "all" ? undefined : categoryId
    router.get(
      "/app/admin/courses",
      buildParams({ category_id: value, page: 1 }),
      { preserveState: true }
    )
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    router.get(
      "/app/admin/courses",
      buildParams({ search: searchQuery || undefined, page: 1 }),
      { preserveState: true }
    )
  }

  // Windowed page numbers
  const getPageNumbers = () => {
    if (!pagination) return []
    const { currentPage, totalPages } = pagination
    const windowSize = 5
    let start = Math.max(1, currentPage - Math.floor(windowSize / 2))
    let end = Math.min(totalPages, start + windowSize - 1)
    if (end - start < windowSize - 1) {
      start = Math.max(1, end - windowSize + 1)
    }
    const pages = []
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Courses - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={coursesTabs} />

        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Courses</h1>
            <p className="text-zinc-500">Manage LMS courses and content</p>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/app/admin/courses/pending_review">
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Clock className="h-4 w-4 mr-2" />
                Pending Review
                {pendingPublicReviewCount > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white hover:bg-amber-500">
                    {pendingPublicReviewCount}
                  </Badge>
                )}
              </Button>
            </Link>
            <Button variant="gradient" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Course
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 grid-cols-3">
          {statCards.map((card) => {
            const count = stats?.[card.statKey] || 0
            const isActive = filters?.currentStatus === card.key
            return (
              <button
                key={card.label}
                onClick={() => handleStatusFilter(card.key)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  isActive
                    ? "border-kf-red bg-red-50"
                    : "border-zinc-200 hover:border-zinc-300 bg-white"
                }`}
              >
                <p className="text-sm text-zinc-500">{card.label}</p>
                <p className="text-2xl font-bold text-zinc-900">{count}</p>
              </button>
            )
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Category filter */}
          <Select
            value={filters?.currentCategory || "all"}
            onValueChange={handleCategoryFilter}
          >
            <SelectTrigger className="w-full sm:w-[200px] bg-white border-zinc-300 text-zinc-900">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map((cat) => (
                <SelectItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          <form
            onSubmit={handleSearchSubmit}
            className="relative max-w-md flex-1"
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              type="search"
              placeholder="Search courses by title..."
              className="pl-9 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchSubmit(e)
              }}
            />
          </form>
        </div>

        {/* Courses Table */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {courses?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <SortableHeader
                      column="title"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500"
                    >
                      Course
                    </SortableHeader>
                    <SortableHeader
                      column="category"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden lg:table-cell"
                    >
                      Category
                    </SortableHeader>
                    <SortableHeader
                      column="status"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden sm:table-cell"
                    >
                      Status
                    </SortableHeader>
                    <SortableHeader
                      column="visibility"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden md:table-cell"
                    >
                      Visibility
                    </SortableHeader>
                    <SortableHeader
                      column="createdBy"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden lg:table-cell"
                    >
                      Created by
                    </SortableHeader>
                    <SortableHeader
                      column="lessons"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden md:table-cell"
                    >
                      Lessons
                    </SortableHeader>
                    <SortableHeader
                      column="updatedAt"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden xl:table-cell"
                    >
                      Updated
                    </SortableHeader>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCourses.map((course) => (
                    <TableRow
                      key={course.id}
                      className="cursor-pointer border-zinc-100 hover:bg-zinc-50"
                      onClick={() =>
                        router.visit(`/app/admin/courses/${course.id}`)
                      }
                    >
                      <TableCell className="whitespace-normal max-w-[280px] sm:max-w-[360px] md:max-w-[400px] lg:max-w-[460px]">
                        <div className="flex items-start gap-3 min-w-0">
                          <CourseCoverImage
                            url={course.coverImageUrl}
                            alt=""
                            wrapperClassName="h-10 w-16 rounded border border-zinc-200 overflow-hidden flex-shrink-0"
                            imgClassName="w-full h-full object-cover"
                            fallbackIconClassName="h-4 w-4 text-zinc-300"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-zinc-900 truncate">
                              {course.title}
                            </p>
                            {course.description && (
                              <p className="text-sm text-zinc-400 truncate">
                                {course.description}
                              </p>
                            )}
                            <div className="mt-1.5 flex items-center gap-2 sm:hidden">
                              <StatusBadge status={course.status} />
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden lg:table-cell">
                        {course.category?.name || "-"}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <StatusBadge status={course.status} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <VisibilityLabel visibility={course.visibility} />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-zinc-500">
                        {course.createdBy ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-zinc-700">
                              {course.createdBy.name}
                            </span>
                            {course.createdBy.isAdmin === false && (
                              <Badge
                                variant="outline"
                                className="border-zinc-300 text-zinc-500 text-[10px] px-1.5 py-0"
                              >
                                Coach
                              </Badge>
                            )}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-zinc-500">
                        {course.lessonsCount}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-zinc-500 text-sm whitespace-nowrap">
                        {formatDate(course.updatedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeleteCourse(course)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <BookOpen className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">No courses found</p>
                <p className="text-sm">
                  {filters?.search || filters?.currentCategory
                    ? "Try adjusting your filters or search term"
                    : "Create your first course to get started"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination &&
          pagination.totalPages > 1 &&
          (() => {
            const pages = getPageNumbers()
            const firstPage = pages[0]
            const lastPage = pages[pages.length - 1]
            const goToPage = (page) =>
              router.get(
                "/app/admin/courses",
                buildParams({ page }),
                { preserveState: true }
              )

            return (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-zinc-500">
                  Showing {pagination.from} &ndash; {pagination.to} of{" "}
                  {pagination.totalCount} courses
                </p>
                <Pagination>
                  <PaginationContent>
                    {pagination.currentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() =>
                            goToPage(pagination.currentPage - 1)
                          }
                        />
                      </PaginationItem>
                    )}
                    {firstPage > 1 && (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => goToPage(1)}>
                            1
                          </PaginationLink>
                        </PaginationItem>
                        {firstPage > 2 && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}
                      </>
                    )}
                    {pages.map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => goToPage(page)}
                          isActive={page === pagination.currentPage}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    {lastPage < pagination.totalPages && (
                      <>
                        {lastPage < pagination.totalPages - 1 && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}
                        <PaginationItem>
                          <PaginationLink
                            onClick={() => goToPage(pagination.totalPages)}
                          >
                            {pagination.totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}
                    {pagination.currentPage < pagination.totalPages && (
                      <PaginationItem>
                        <PaginationNext
                          onClick={() =>
                            goToPage(pagination.currentPage + 1)
                          }
                        />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              </div>
            )
          })()}
      </div>

      {/* Create Dialog */}
      <CourseFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        categories={categories}
      />

      {/* Delete Dialog */}
      <DeleteCourseDialog
        open={!!deleteCourse}
        onOpenChange={(open) => {
          if (!open) setDeleteCourse(null)
        }}
        course={deleteCourse}
      />
    </AdminLayout>
  )
}
